/**
 * Utility functions for generating hashes and managing caching
 */

/**
 * Generate a simple hash for data to enable caching
 * @param data - The data to hash
 * @returns A hash string
 */
export const generateDataHash = (data: any): string => {
  const dataString = JSON.stringify(data);
  let hash = 0;

  if (dataString.length === 0) return hash.toString();

  for (let index = 0; index < dataString.length; index += 1) {
    const char = dataString.codePointAt(index) || 0;
    hash = hash * 33 - hash + char;
    // eslint-disable-next-line no-bitwise
    hash &= 0xff_ff_ff_ff; // Convert to 32-bit integer
  }

  return hash.toString();
};

/**
 * Simple cache implementation with size limits
 */
export class SimpleCache<K, V> {
  private cache = new Map<K, V>();

  private maxSize: number;

  constructor(maxSize: number = 50) {
    this.maxSize = maxSize;
  }

  set(key: K, value: V): void {
    this.cache.set(key, value);

    // Limit cache size to prevent memory issues
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
  }

  get(key: K): V | undefined {
    return this.cache.get(key);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}
